import { Injectable } from '@angular/core';
import { AD_SLOTS } from '../constants/ad-slots.config';
import { AD_DEBUG_KEY, adsAllowedForHost } from '../constants/ads.config';
import { DeviceService } from './tv-detect.service';

@Injectable({ providedIn: 'root' })
export class AdResolverService {
  constructor(private device: DeviceService) {}

  /**
   * Ads render when the kill switch is on AND the host is on the allowlist
   * (see ads.config). The hostname was previously hardcoded to `toqqer.com`,
   * which meant this white-label build never served an ad on its own domain.
   *
   * A getter, not a constructor field, so the `adsDebug` override can be flipped
   * from the console without a reload.
   */
  get showAds(): boolean {
    return adsAllowedForHost(window.location.hostname, this.debugOverride());
  }

  /**
   * Dev escape hatch: `localStorage.setItem('adsDebug', '1')` renders the ad
   * placements on any host. Cannot override ADS_ENABLED. Reading localStorage
   * throws in some WebViews (private mode, blocked storage), and an ad flag is
   * never worth taking the app down for.
   */
  private debugOverride(): boolean {
    try {
      return localStorage.getItem(AD_DEBUG_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Which ad belongs above section `index`, or null for none.
   *
   * `isMobileView` should be the same breakpoint the caller is rendering with —
   * pass it in rather than letting this re-read the window width, or the two can
   * disagree mid-resize and the native slot renders an empty gap. Defaults to a
   * live read for callers that have no view state of their own.
   */
  getAdTypeForIndex(index: number, isMobileView = this.device.isMobile()): 'banner' | 'native' | null {
    if (!this.showAds) return null;
    const slot = AD_SLOTS.find(s => s.afterIndex === index);
    if (!slot) return null;
    // Native ads are mobile-only; desktop falls back to a banner in that slot.
    if (slot.type === 'native' && !isMobileView) {
      return 'banner';
    }
    return slot.type;
  }
}
